// StepSelectChartType.jsx
import React from "react";
import {
  Box,
  Checkbox,
  FormControlLabel,
  Card,
  CardContent,
  Switch,
  Divider,
  FormGroup,
  Radio,
  RadioGroup,
} from "@mui/material";
import Label from "../../../components/Label/Label";
import {
  distributionCurvesOptions,
  plotTypeOptions,
  ruleOptions,
} from "../visualization/constants";
import RuleSelector from "../visualization/controlChart/RuleSelector";

// --- Default configs
const defaultControlConfig = {
  movingAverage: false,
  plotAggregation: false,
  ruleset: "nelson",
  selectedRules: ["rule1"],
};
const defaultPCCConfig = {};
const defaultXBarConfig = {
  selectedBatchId: "",
  ruleset: "nelson",
  selectedRules: ["rule1"],
};
const defaultPPConfig = {
  selectedCurves: ["norm"],
};

const StepSelectChartType = ({
  chartState = {},
  onChange,
  batchIdList = [],
}) => {
  const {
    plotTypes = [],
    controlChartConfigState = { ...defaultControlConfig },
    ppChartConfigState = { ...defaultPPConfig },
    pccChartConfigState = { ...defaultPCCConfig },
    xBarChartConfigState = { ...defaultXBarConfig },
  } = chartState;

  const updateChartState = (field, value) => {
    onChange({ [field]: value });
  };

  const updateControlConfig = (field, value) => {
    updateChartState("controlChartConfigState", {
      ...controlChartConfigState,
      [field]: value,
    });
  };

  const updatePPConfig = (field, value) => {
    updateChartState("ppChartConfigState", {
      ...ppChartConfigState,
      [field]: value,
    });
  };

  const updateXBarConfig = (field, value) => {
    updateChartState("xBarChartConfigState", {
      ...xBarChartConfigState,
      [field]: value,
    });
  };

  // --- Handle Plot Type checkbox toggle
  const handlePlotTypeToggle = (type) => {
    let updated = [...plotTypes];

    if (updated.includes(type)) {
      // Uncheck: remove type & reset config
      updated = updated.filter((t) => t !== type);
      switch (type) {
        case "control_chart":
          updateChartState("controlChartConfigState", {
            ...defaultControlConfig,
          });
          break;
        case "pcc_chart":
          updateChartState("pccChartConfigState", { ...defaultPCCConfig });
          break;
        case "xbar_chart":
          updateChartState("xBarChartConfigState", { ...defaultXBarConfig });
          break;
        case "pp_chart":
          updateChartState("ppChartConfigState", { ...defaultPPConfig });
          break;
      }
    } else {
      // Check: add type & initialize config
      updated.push(type);
      switch (type) {
        case "control_chart":
          updateChartState("controlChartConfigState", {
            ...defaultControlConfig,
          });
          break;
        case "pcc_chart":
          updateChartState("pccChartConfigState", { ...defaultPCCConfig });
          break;
        case "xbar_chart":
          updateChartState("xBarChartConfigState", {
            ...defaultXBarConfig,
            selectedBatchId: batchIdList[0] || "",
          });
          break;
        case "pp_chart":
          updateChartState("ppChartConfigState", { ...defaultPPConfig });
          break;
      }
    }

    updateChartState("plotTypes", updated);
  };

  return (
    <Box>
      <Label text="Chart Type" fontWeight="700" fontSize="14px" />

      <FormGroup sx={{ pt: 1 }}>
        {plotTypeOptions.map((opt) => (
          <Box key={opt.value}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={plotTypes.includes(opt.value)}
                  onChange={() => handlePlotTypeToggle(opt.value)}
                />
              }
              label={<Label text={opt.label} fontWeight="500" />}
            />

            {/* --- Control Chart Config --- */}
            {plotTypes.includes("control_chart") &&
              opt.value === "control_chart" && (
                <Box ml={4}>
                  <Card elevation={0}>
                    <CardContent>
                      <FormControlLabel
                        sx={{ pl: 1, pr: 1 }}
                        control={
                          <Switch
                            checked={controlChartConfigState.movingAverage}
                            onChange={(e) =>
                              updateControlConfig(
                                "movingAverage",
                                e.target.checked
                              )
                            }
                          />
                        }
                        label={<Label text="Moving Range" fontWeight="500" />}
                      />
                      <Divider sx={{ my: 1 }} />
                      <FormControlLabel
                        sx={{ pl: 1, pr: 1 }}
                        control={
                          <Switch
                            checked={controlChartConfigState.plotAggregation}
                            onChange={(e) =>
                              updateControlConfig(
                                "plotAggregation",
                                e.target.checked
                              )
                            }
                          />
                        }
                        label={
                          <Label
                            text="Plot Data Aggregation"
                            fontWeight="500"
                          />
                        }
                      />
                    </CardContent>
                  </Card>

                  <Card elevation={0} sx={{ mt: 0 }}>
                    <CardContent sx={{ mt: 0, pt: 0 }}>
                      <Label text="Nelson Rules" fontWeight="500" />
                      <RuleSelector
                        ruleOptions={ruleOptions}
                        selectedRules={controlChartConfigState.selectedRules}
                        setSelectedRules={(val) =>
                          updateControlConfig("selectedRules", val)
                        }
                      />
                    </CardContent>
                  </Card>
                </Box>
              )}

            {/* --- X-Bar Chart Config --- */}
            {plotTypes.includes("xbar_chart") && opt.value === "xbar_chart" && (
              <Box ml={4}>
                <Card elevation={0}>
                  <CardContent>
                    <Label text="Select Batch ID" fontWeight="500" />

                    <Box
                      variant="outlined"
                      sx={{
                        maxHeight: 300,
                        overflow: "auto",
                      }}
                    >
                      <RadioGroup
                        value={xBarChartConfigState.selectedBatchId || ""}
                        onChange={(e) =>
                          updateXBarConfig("selectedBatchId", e.target.value)
                        }
                      >
                        {batchIdList.map((batchId) => (
                          <FormControlLabel
                            key={batchId}
                            value={batchId}
                            control={<Radio />}
                            label={batchId}
                          />
                        ))}
                      </RadioGroup>
                    </Box>
                  </CardContent>
                </Card>

                <Card elevation={0} sx={{ mt: 1 }}>
                  <CardContent sx={{ mt: 0, pt: 0 }}>
                    <Label text="Nelson Rules" fontWeight="500" />
                    <RuleSelector
                      ruleOptions={ruleOptions}
                      selectedRules={xBarChartConfigState.selectedRules}
                      setSelectedRules={(val) =>
                        updateXBarConfig("selectedRules", val)
                      }
                    />
                  </CardContent>
                </Card>
              </Box>
            )}

            {/* --- PP Chart Config --- */}
            {plotTypes.includes("pp_chart") && opt.value === "pp_chart" && (
              <Box ml={4}>
                <Card elevation={0}>
                  <CardContent>
                    <Label text="Select Distribution Curves" fontWeight="500" />
                    <FormGroup>
                      {distributionCurvesOptions.map((curve) => {
                        const isLocked = curve.value === "norm";
                        const selected =
                          ppChartConfigState.selectedCurves || [];

                        const handleToggle = () => {
                          let updated = selected.includes(curve.value)
                            ? selected.filter((c) => c !== curve.value)
                            : [...selected, curve.value];

                          // Ensure "norm" is always selected
                          updated = Array.from(new Set([...updated, "norm"]));
                          updatePPConfig("selectedCurves", updated);
                        };

                        return (
                          <FormControlLabel
                            key={curve.value}
                            control={
                              <Checkbox
                                checked={selected.includes(curve.value)}
                                onChange={handleToggle}
                                disabled={isLocked}
                              />
                            }
                            label={curve.label}
                            sx={
                              isLocked
                                ? {
                                    backgroundColor: "#f5f5f5",
                                    borderRadius: 1,
                                    pl: 1,
                                  }
                                : {}
                            }
                          />
                        );
                      })}
                    </FormGroup>
                  </CardContent>
                </Card>
              </Box>
            )}

            <Divider sx={{ my: 1 }} />
          </Box>
        ))}
      </FormGroup>
    </Box>
  );
};

export default StepSelectChartType;
